import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ListagemUsuarios, FiltrosUsuarios } from '../model/usuario.model';
import { environment } from '../../../environments/environment';
import { PageModel } from '../models/pagination/page.model';
import { UsuarioResponseDto } from '../models/response/usuario-response.model';
import { UsuarioRequest } from '../models/request/usuario-request.model';

@Injectable({
  providedIn: 'root',
})
export class UsuarioService {
  private apiUrl = `${environment.fullApiUrl}`;
  private usuariosUrl = `${this.apiUrl}/usuario`;
  private http = inject(HttpClient);

  /**
   * Obtém a lista de todos os usuários (sem filtros)
   */
  obterTodos(): Observable<ListagemUsuarios> {
    return this.http.get<ListagemUsuarios>(this.usuariosUrl);
  }

  /**
   * Obtém uma lista filtrada de usuários (para pesquisa)
   */
  obterUsuariosFiltrados(
    filtros: FiltrosUsuarios = {},
  ): Observable<ListagemUsuarios> {
    let params = new HttpParams();

    if (filtros.nome) {
      params = params.set('nome', filtros.nome);
    }
    if (filtros.cpf) {
      params = params.set('cpf', filtros.cpf);
    }
    if (filtros.perfil) {
      params = params.set('perfil', filtros.perfil);
    }
    if (filtros.status !== undefined && filtros.status !== null) {
      params = params.set('status', filtros.status.toString());
    }

    return this.http.get<ListagemUsuarios>(`${this.usuariosUrl}/pesquisar`, {
      params,
    });
  }

  /**
   * Busca paginada de usuários similar ao consultarPerfis (para listagem)
   * @param filtros Filtros opcionais para a busca
   * @param page Número da página (padrão: 0)
   * @param size Tamanho da página (padrão: 10)
   * @returns Observable com lista paginada de usuários
   */
  buscaPaginada(
    filtros: FiltrosUsuarios = {},
    page: number = 0,
    size: number = 10,
  ): Observable<ListagemUsuarios> {
    const filterParts: string[] = [];

    if (
      filtros?.status !== undefined &&
      filtros.status !== null &&
      filtros.status !== ''
    ) {
      if (filtros.status === true || filtros.status === 'true') {
        filterParts.push('status==S');
      } else if (filtros.status === false || filtros.status === 'false') {
        filterParts.push('status==N');
      }
    }

    if (filtros?.nome && filtros.nome.trim() !== '') {
      filterParts.push(`nome==${filtros.nome.trim()}`);
    }

    if (filtros?.cpf && filtros.cpf.trim() !== '') {
      filterParts.push(`cpf==${filtros.cpf.trim()}`);
    }

    if (filtros?.perfil && filtros.perfil !== '') {
      if (typeof filtros.perfil === 'number') {
        filterParts.push(`perfilIds==${filtros.perfil}`);
      } else {
        filterParts.push(`perfilNomes==${filtros.perfil.trim()}`);
      }
    }

    if (filtros?.esfera && filtros.esfera.trim() !== '') {
      filterParts.push(`esferaAtuacao==${filtros.esfera.trim()}`);
    }

    if (filtros?.coUfIbge && filtros.coUfIbge.trim() !== '') {
      filterParts.push(`coUfIbge==${filtros.coUfIbge.trim()}`);
    }

    if (filtros?.coMunicipioIbge && filtros.coMunicipioIbge.trim() !== '') {
      filterParts.push(`coMunicipioIbge==${filtros.coMunicipioIbge.trim()}`);
    }

    const filterString = filterParts.join(';');

    const params = new HttpParams()
      .set('filter', filterString)
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', 'nome,asc');

    return this.http
      .get<
        PageModel<UsuarioResponseDto>
      >(`${this.usuariosUrl}/search`, { params })
      .pipe(
        map((response) => {
          const usuarios = response.content || [];

          let usuariosFiltrados = usuarios;

          if (
            filtros?.perfil &&
            typeof filtros.perfil === 'string' &&
            filtros.perfil.trim() !== ''
          ) {
            usuariosFiltrados = usuariosFiltrados.filter((usuario) => {
              return usuario.relPerfis?.some((perfil) =>
                perfil.perfil.nome
                  ?.toLowerCase()
                  .includes(filtros.perfil!.toString().toLowerCase()),
              );
            });

            const totalFiltrado = usuariosFiltrados.length;
            const totalPaginas = Math.ceil(totalFiltrado / size);
            const indiceInicio = page * size;
            const indiceFim = indiceInicio + size;
            const resultadosPaginados = usuariosFiltrados.slice(
              indiceInicio,
              indiceFim,
            );

            return {
              usuarios: resultadosPaginados,
              paginacao: {
                pagina: page,
                tamanhoPagina: size,
                total: totalFiltrado,
                totalPaginas: totalPaginas,
              },
            };
          }

          return {
            usuarios: usuariosFiltrados,
            paginacao: {
              pagina: response.page.number || page,
              tamanhoPagina: response.page.size || size,
              total: response.page.totalElements || 0,
              totalPaginas: response.page.totalPages || 0,
            },
          };
        }),
        catchError((error) => {
          console.error('Erro ao buscar usuários paginados:', error);
          return of({
            usuarios: [],
            paginacao: {
              pagina: page,
              tamanhoPagina: size,
              total: 0,
              totalPaginas: 0,
            },
          });
        }),
      );
  }

  /**
   * Obtém um usuário pelo ID
   * @param id ID do usuário
   */
  obterPorId(id: number): Observable<UsuarioResponseDto> {
    return this.http.get<UsuarioResponseDto>(`${this.usuariosUrl}/${id}`);
  }

  obterPorCPF(cpf: string): Observable<UsuarioResponseDto> {
    return this.http.get<UsuarioResponseDto>(
      `${this.usuariosUrl}/obter-por-cpf/${cpf}`,
    );
  }

  /**
   * Cria um novo usuário
   * @param usuario Dados do usuário a ser criado
   */
  criar(usuario: UsuarioRequest): Observable<UsuarioResponseDto> {
    return this.http.post<UsuarioResponseDto>(`${this.usuariosUrl}`, usuario);
  }

  /**
   * Atualiza um usuário existente
   * @param id ID do usuário
   * @param usuario Dados atualizados do usuário
   */
  atualizar(
    id: number,
    usuario: UsuarioRequest,
  ): Observable<UsuarioResponseDto> {
    return this.http.put<UsuarioResponseDto>(
      `${this.usuariosUrl}/${id}`,
      usuario,
    );
  }

  inativar(id: number): Observable<void> {
    return this.http.delete<void>(`${this.usuariosUrl}/inactivate/${id}`);
  }

  ativar(id: number): Observable<void> {
    return this.http.patch<void>(`${this.usuariosUrl}/activate/${id}`, {});
  }

  current(): Observable<UsuarioResponseDto> {
    return this.http.get<UsuarioResponseDto>(`${this.usuariosUrl}/current`);
  }

  pesquisarAnalistas(nome: string, perfilIds: number = 1): Observable<UsuarioResponseDto[]> {
    const params = new HttpParams()
      .set('perfilIds', String(perfilIds))
      .set('status', 'true')
      .set('nome', nome);
    return this.http.get<any>(`${this.usuariosUrl}/pesquisar`, { params }).pipe(
      map((res) => {
        if (Array.isArray(res)) return res as UsuarioResponseDto[];
        if (res?.usuarios) return res.usuarios as UsuarioResponseDto[];
        if (res?.content) return res.content as UsuarioResponseDto[];
        return [];
      }),
      catchError(() => of([])),
    );
  }
}
